import { useEffect, useState, useRef } from 'react';
import { Copy, X, Minus, Plus } from 'lucide-react';

const NORM = 125; // matches the NORM constant inside each 3D preview (world unit ↔ cm)

// Reads live state from the OrbitControls instance attached to the preview canvas.
// Each 3D preview exposes `canvas.__orbitControls` on mount — we just discover it.
const readSnapshot = () => {
  const canvas = document.querySelector('canvas');
  const ctrls = canvas && canvas.__orbitControls;
  if (!ctrls) return null;
  // Derive product name from URL slug, e.g. "/customise/round-bench-seat-cushion" → "Round Cushion"
  const slug = (window.location.pathname.split('/').filter(Boolean).pop() || '').toLowerCase();
  const product_name = slug
    .split('-')
    .map(w => w ? w[0].toUpperCase() + w.slice(1) : w)
    .join(' ') || 'Unknown';
  const cam = ctrls.object;
  const tgt = ctrls.target;
  const pos = cam.position;
  const dx = pos.x - tgt.x, dy = pos.y - tgt.y, dz = pos.z - tgt.z;
  const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
  const azimuth = (Math.atan2(dx, dz) * 180) / Math.PI;
  const polar = (Math.acos(distance ? dy / distance : 0) * 180) / Math.PI;
  const rect = canvas.getBoundingClientRect();
  return {
    product: product_name,
    viewport: rect.width < 500 ? 'mobile' : 'desktop',
    container_w_px: Math.round(rect.width),
    container_h_px: Math.round(rect.height),
    cam_pos: {
      x: +pos.x.toFixed(4), y: +pos.y.toFixed(4), z: +pos.z.toFixed(4),
      x_cm: +(pos.x * NORM).toFixed(2), y_cm: +(pos.y * NORM).toFixed(2), z_cm: +(pos.z * NORM).toFixed(2),
    },
    cam_target: {
      x: +tgt.x.toFixed(4), y: +tgt.y.toFixed(4), z: +tgt.z.toFixed(4),
      x_cm: +(tgt.x * NORM).toFixed(2), y_cm: +(tgt.y * NORM).toFixed(2), z_cm: +(tgt.z * NORM).toFixed(2),
    },
    distance: +distance.toFixed(4),
    distance_cm: +(distance * NORM).toFixed(2),
    azimuth_deg: +azimuth.toFixed(2),
    polar_deg: +polar.toFixed(2),
    fov: cam.fov,
    aspect: +(cam.aspect || rect.width / Math.max(rect.height, 1)).toFixed(3),
  };
};

const Row = ({ label, value, mono = true }) => (
  <div className="flex justify-between gap-3 leading-tight">
    <span className="text-gray-400">{label}</span>
    <span className={mono ? 'font-mono text-emerald-300' : 'text-emerald-300'}>{value}</span>
  </div>
);

// Nudges the camera/target/angle by 1 cm or 1° per click. Reads/writes the
// OrbitControls instance attached to the preview canvas, then dispatches a
// synthetic "start" event so the preview's auto-reset logic stops fighting us.
const CM_STEP = 1 / NORM;
const DEG_STEP = (1 * Math.PI) / 180;
const applyDelta = (field, sign) => {
  const canvas = document.querySelector('canvas');
  const ctrls = canvas && canvas.__orbitControls;
  if (!ctrls) return;
  const cam = ctrls.object;
  const tgt = ctrls.target;
  const dx = cam.position.x - tgt.x;
  const dy = cam.position.y - tgt.y;
  const dz = cam.position.z - tgt.z;
  const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
  switch (field) {
    case 'cam_x': cam.position.x += sign * CM_STEP; break;
    case 'cam_y': cam.position.y += sign * CM_STEP; break;
    case 'cam_z': cam.position.z += sign * CM_STEP; break;
    case 'tgt_x': tgt.x += sign * CM_STEP; break;
    case 'tgt_y': tgt.y += sign * CM_STEP; break;
    case 'tgt_z': tgt.z += sign * CM_STEP; break;
    case 'distance': {
      if (dist < 0.0001) break;
      const k = (dist + sign * CM_STEP) / dist;
      cam.position.set(tgt.x + dx * k, tgt.y + dy * k, tgt.z + dz * k);
      break;
    }
    case 'azimuth': {
      const cos = Math.cos(sign * DEG_STEP);
      const sin = Math.sin(sign * DEG_STEP);
      cam.position.x = tgt.x + dx * cos - dz * sin;
      cam.position.z = tgt.z + dx * sin + dz * cos;
      break;
    }
    case 'polar': {
      if (dist < 0.0001) break;
      const az = Math.atan2(dx, dz);
      const po = Math.acos(dy / dist);
      const newPo = Math.min(Math.PI - 0.01, Math.max(0.01, po + sign * DEG_STEP));
      cam.position.x = tgt.x + dist * Math.sin(newPo) * Math.sin(az);
      cam.position.y = tgt.y + dist * Math.cos(newPo);
      cam.position.z = tgt.z + dist * Math.sin(newPo) * Math.cos(az);
      break;
    }
    default: break;
  }
  // Pretend the user touched the canvas so the preview's reset logic stays out
  ctrls.dispatchEvent({ type: 'start' });
  ctrls.update();
  if (canvas.__renderFrame) canvas.__renderFrame();
};

// Screen-space pan: moves BOTH the camera AND target by the same screen-space
// vector (1 cm per click). This is what right-click-drag does in OrbitControls
// — the cushion appears to slide up/down/left/right inside the viewport without
// any rotation. Direction: 'up' | 'down' | 'left' | 'right'.
const applyPan = (direction) => {
  const canvas = document.querySelector('canvas');
  const ctrls = canvas && canvas.__orbitControls;
  if (!ctrls) return;
  const cam = ctrls.object;
  const tgt = ctrls.target;
  // Camera forward (target - camera, unit vector)
  const fx = tgt.x - cam.position.x;
  const fy = tgt.y - cam.position.y;
  const fz = tgt.z - cam.position.z;
  const fl = Math.sqrt(fx * fx + fy * fy + fz * fz) || 1;
  const fwd = { x: fx / fl, y: fy / fl, z: fz / fl };
  // World up — typically (0, 1, 0) for OrbitControls
  const upWorld = cam.up || { x: 0, y: 1, z: 0 };
  // Screen-right = forward × up (cross product). Three.js right-handed.
  const rx = fwd.y * upWorld.z - fwd.z * upWorld.y;
  const ry = fwd.z * upWorld.x - fwd.x * upWorld.z;
  const rz = fwd.x * upWorld.y - fwd.y * upWorld.x;
  const rl = Math.sqrt(rx * rx + ry * ry + rz * rz) || 1;
  const right = { x: rx / rl, y: ry / rl, z: rz / rl };
  // Screen-up = right × forward (re-orthogonalised so it's perpendicular to fwd)
  const ux = right.y * fwd.z - right.z * fwd.y;
  const uy = right.z * fwd.x - right.x * fwd.z;
  const uz = right.x * fwd.y - right.y * fwd.x;
  const ul = Math.sqrt(ux * ux + uy * uy + uz * uz) || 1;
  const up = { x: ux / ul, y: uy / ul, z: uz / ul };
  // Determine offset axis + sign — note: panning the camera one way makes the
  // cushion appear to move the OPPOSITE way, so we flip signs to match the
  // intuitive "click up = cushion moves up on screen" mapping.
  let ax = 0, ay = 0, az = 0;
  if (direction === 'up') { ax = -up.x; ay = -up.y; az = -up.z; }
  else if (direction === 'down') { ax = up.x; ay = up.y; az = up.z; }
  else if (direction === 'left') { ax = right.x; ay = right.y; az = right.z; }
  else if (direction === 'right') { ax = -right.x; ay = -right.y; az = -right.z; }
  ax *= CM_STEP; ay *= CM_STEP; az *= CM_STEP;
  cam.position.x += ax; cam.position.y += ay; cam.position.z += az;
  tgt.x += ax; tgt.y += ay; tgt.z += az;
  ctrls.dispatchEvent({ type: 'start' });
  ctrls.update();
  if (canvas.__renderFrame) canvas.__renderFrame();
};

const PanArrow = ({ label, dir }) => (
  <button
    onClick={() => applyPan(dir)}
    className="w-7 h-7 flex items-center justify-center bg-white/5 hover:bg-emerald-400/30 rounded text-white/80 hover:text-white text-[14px] leading-none font-bold transition-colors"
  >
    {label}
  </button>
);

const PanPad = () => (
  <div className="flex items-start gap-3 my-1">
    <div className="flex flex-col items-center gap-1">
      <div className="text-gray-400 text-[10px] uppercase tracking-wider self-start mb-0.5">Pan (1cm)</div>
      <PanArrow label="↑" dir="up" />
      <div className="flex gap-1">
        <PanArrow label="←" dir="left" />
        <PanArrow label="↓" dir="down" />
        <PanArrow label="→" dir="right" />
      </div>
    </div>
    <div className="flex flex-col items-center gap-1">
      <div className="text-gray-400 text-[10px] uppercase tracking-wider self-start mb-0.5">Zoom</div>
      <button
        onClick={() => applyDelta('distance', -1)}
        className="w-10 h-10 flex items-center justify-center bg-white/5 hover:bg-emerald-400/30 active:bg-emerald-400/50 rounded text-white text-[20px] leading-none font-bold transition-colors"
        style={{ touchAction: 'manipulation' }}
        title="Zoom in (move camera closer)"
      >
        +
      </button>
      <button
        onClick={() => applyDelta('distance', +1)}
        className="w-10 h-10 flex items-center justify-center bg-white/5 hover:bg-emerald-400/30 active:bg-emerald-400/50 rounded text-white text-[20px] leading-none font-bold transition-colors"
        style={{ touchAction: 'manipulation' }}
        title="Zoom out (move camera away)"
      >
        −
      </button>
    </div>
  </div>
);

const StepBtn = ({ label, onClick }) => (
  <button
    onClick={onClick}
    className="w-7 h-7 flex items-center justify-center bg-white/5 hover:bg-emerald-400/30 active:bg-emerald-400/50 rounded text-white/80 hover:text-white text-[16px] leading-none font-bold transition-colors"
    style={{ touchAction: 'manipulation' }}
  >
    {label}
  </button>
);

const AdjRow = ({ label, value, field }) => (
  <div className="flex items-center justify-between gap-2 leading-tight">
    <span className="text-gray-400 flex-shrink-0">{label}</span>
    <div className="flex items-center gap-1.5 ml-auto">
      <StepBtn label="−" onClick={() => applyDelta(field, -1)} />
      <span className="font-mono text-emerald-300 min-w-[110px] text-right text-[10.5px]">{value}</span>
      <StepBtn label="+" onClick={() => applyDelta(field, +1)} />
    </div>
  </div>
);

export default function CameraDebugHUD() {
  const [snap, setSnap] = useState(null);
  const [collapsed, setCollapsed] = useState(true);
  const [hidden, setHidden] = useState(false);
  const [copied, setCopied] = useState(false);
  // Position state — once the user drags, we switch from CSS top/left to absolute coords
  const [pos, setPos] = useState(null); // {x, y} in viewport pixels, or null = use defaults
  const dragRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    let mounted = true;
    const tick = () => {
      if (!mounted) return;
      const next = readSnapshot();
      if (next) setSnap(next);
      rafRef.current = requestAnimationFrame(tick);
    };
    tick();
    return () => { mounted = false; cancelAnimationFrame(rafRef.current); };
  }, []);

  // Pointer-based drag: works for mouse + touch with one event handler set
  const onPointerDown = (e) => {
    e.preventDefault();
    const rect = e.currentTarget.parentElement.parentElement.getBoundingClientRect();
    dragRef.current = {
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const onMove = (ev) => {
      if (!dragRef.current) return;
      const x = Math.max(0, Math.min(window.innerWidth - 60, ev.clientX - dragRef.current.offsetX));
      const y = Math.max(0, Math.min(window.innerHeight - 40, ev.clientY - dragRef.current.offsetY));
      setPos({ x, y });
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const handleCopy = () => {
    if (!snap) return;
    navigator.clipboard.writeText(JSON.stringify(snap, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };

  if (hidden) return null;

  return (
    <div
      className={pos ? 'fixed z-[9999] text-[11px] text-white' : 'fixed bottom-3 left-1/2 -translate-x-1/2 z-[9999] text-[11px] text-white'}
      style={{
        background: 'rgba(15, 23, 42, 0.92)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: '8px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
        maxWidth: 'min(280px, calc(100vw - 24px))',
        ...(pos ? { left: pos.x, top: pos.y, transform: 'none' } : {}),
      }}
      data-testid="camera-debug-hud"
    >
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-white/10">
        <span
          onPointerDown={onPointerDown}
          className="text-[10px] uppercase tracking-wider font-semibold text-emerald-300 select-none cursor-move flex-1"
          style={{ touchAction: 'none' }}
          data-testid="hud-drag-handle"
        >
          ⋮⋮ Camera Debug
        </span>
        <div className="flex gap-1">
          <button
            onClick={handleCopy}
            disabled={!snap}
            className="text-gray-400 hover:text-white p-1 disabled:opacity-50"
            title="Copy snapshot to clipboard"
            data-testid="hud-copy"
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setCollapsed(c => !c)}
            className="text-gray-400 hover:text-white p-1"
            title={collapsed ? 'Expand' : 'Minimise'}
            data-testid="hud-minimise"
          >
            {collapsed ? <Plus className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />}
          </button>
          <button
            onClick={() => setHidden(true)}
            className="text-gray-400 hover:text-white p-1"
            title="Hide HUD"
            data-testid="hud-close"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="px-3 py-2 space-y-1">
          {!snap ? (
            <p className="text-gray-400">Searching for 3D canvas…</p>
          ) : (
            <>
              <Row label="Product" value={snap.product} mono={false} />
              <Row label="Viewport" value={`${snap.viewport} (${snap.container_w_px}×${snap.container_h_px})`} mono={false} />
              <div className="border-t border-white/10 my-1.5" />
              <PanPad />
              <div className="border-t border-white/10 my-1.5" />
              <div className="text-gray-400 text-[10px] uppercase tracking-wider">Camera position</div>
              <AdjRow label="x" field="cam_x" value={`${snap.cam_pos.x}  (${snap.cam_pos.x_cm}cm)`} />
              <AdjRow label="y" field="cam_y" value={`${snap.cam_pos.y}  (${snap.cam_pos.y_cm}cm)`} />
              <AdjRow label="z" field="cam_z" value={`${snap.cam_pos.z}  (${snap.cam_pos.z_cm}cm)`} />
              <div className="border-t border-white/10 my-1.5" />
              <div className="text-gray-400 text-[10px] uppercase tracking-wider">Look-at target</div>
              <AdjRow label="x" field="tgt_x" value={`${snap.cam_target.x}  (${snap.cam_target.x_cm}cm)`} />
              <AdjRow label="y" field="tgt_y" value={`${snap.cam_target.y}  (${snap.cam_target.y_cm}cm)`} />
              <AdjRow label="z" field="tgt_z" value={`${snap.cam_target.z}  (${snap.cam_target.z_cm}cm)`} />
              <div className="border-t border-white/10 my-1.5" />
              <AdjRow label="Distance" field="distance" value={`${snap.distance}  (${snap.distance_cm}cm)`} />
              <AdjRow label="Azimuth" field="azimuth" value={`${snap.azimuth_deg}°`} />
              <AdjRow label="Polar" field="polar" value={`${snap.polar_deg}°`} />
              <Row label="FOV" value={`${snap.fov}°`} />
              <Row label="Aspect" value={snap.aspect} />
              {copied && <p className="mt-2 text-emerald-400 text-[10px]">✓ Copied to clipboard</p>}
            </>
          )}
        </div>
      )}
    </div>
  );
}
